import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  getPublicTracking,
  PublicOrderTracking,
  PublicTrackingAddress,
} from "../../../api/publicTracking";
import logibudLogo from "../../../assets/logo/logibud_primary_darkblue.svg";
import "./PublicOrderTrackingPage.css";
import OrderTrackingStops from "./OrderTrackingStops";

const steps = [
  ["Ordre bekreftet", "Vi har mottatt og bekreftet ordren din."],
  ["Leveringen er planlagt", "Henting og levering er lagt inn i planen."],
  ["Henting pågår", "Forsendelsen klargjøres og lastes på bilen."],
  ["På vei til deg", "Forsendelsen er hentet og underveis."],
  ["Levert", "Forsendelsen er levert til mottaker."],
] as const;
const statusIndex = {
  RECEIVED: 0,
  SCHEDULED: 1,
  PICKUP_IN_PROGRESS: 2,
  IN_TRANSIT: 3,
  DELIVERED: 4,
  CANCELLED: 0,
};
const statusLabels = {
  RECEIVED: "Ordre bekreftet",
  SCHEDULED: "Planlagt",
  PICKUP_IN_PROGRESS: "Henting pågår",
  IN_TRANSIT: "Underveis",
  DELIVERED: "Levert",
  CANCELLED: "Kansellert",
};

function formatAddress(address?: PublicTrackingAddress | null) {
  if (!address) return "Ikke oppgitt";
  return (
    [
      address.addressLine,
      [address.postCode, address.city].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ") || "Ikke oppgitt"
  );
}
function formatDate(value?: string | null) {
  if (!value) return "Ikke fastsatt";
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? "Ikke fastsatt"
    : new Intl.DateTimeFormat("nb-NO", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(parsed);
}
function DotIcon({ check }: { check: boolean }) {
  return check ? (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="m5 12 4.2 4.2L19 6.5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-current" />
  );
}

export default function PublicOrderTrackingPage() {
  const [tracking, setTracking] = useState<PublicOrderTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [carrierLogoFailed, setCarrierLogoFailed] = useState(false);
  let token = "";
  try {
    token = decodeURIComponent(
      window.location.hash.slice(1).replace(/^token=/, ""),
    );
  } catch {
    // Treat malformed fragments exactly like other invalid tracking links.
  }
  const load = useCallback(async () => {
    if (!token.startsWith("lgb_track_")) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const nextTracking = await getPublicTracking(token);
      setCarrierLogoFailed(false);
      setTracking(nextTracking);
    } catch (error) {
      setNotFound(
        axios.isAxiosError(error) ? error.response?.status !== 200 : true,
      );
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => {
    document.title = "Spor levering | Logibud";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow, noarchive";
    document.head.appendChild(robots);
    void load();
    return () => robots.remove();
  }, [load]);

  if (loading) return <StateView title="Henter leveringsstatus …" pulse />;
  if (notFound || !tracking)
    return (
      <StateView
        title="Sporingen er ikke tilgjengelig"
        text="Lenken kan være utløpt eller deaktivert. Kontakt avsender hvis du trenger en ny sporingslenke."
      />
    );
  const current = statusIndex[tracking.status];
  const cancelled = tracking.status === "CANCELLED";
  const deliveries =
    tracking.stops?.filter((stop) => stop.type === "DELIVERY") || [];
  const multipleDeliveries = deliveries.length > 1;
  const showCarrierLogo = Boolean(
    tracking.carrierLogoUrl?.trim() && !carrierLogoFailed,
  );

  return (
    <main className="tracking-page pb-14">
      <div className="tracking-shell py-5 sm:py-8">
        <header className="mb-5 flex items-center justify-between px-1">
          <img
            alt={
              showCarrierLogo
                ? `Logo for ${tracking.carrierName || "transportøren"}`
                : "Logibud"
            }
            className="h-7 max-w-[180px] object-contain sm:h-8"
            src={showCarrierLogo ? tracking.carrierLogoUrl! : logibudLogo}
            referrerPolicy="no-referrer"
            onError={
              showCarrierLogo ? () => setCarrierLogoFailed(true) : undefined
            }
          />
          <div className="text-xs text-[#706879] sm:text-sm">
            Trygg leveringssporing
          </div>
        </header>
        <section className="tracking-card tracking-hero">
          <div className="p-6 sm:p-9 lg:p-11">
            <p className="mb-2 text-xs font-bold uppercase tracking-[.16em] text-[#7253a6]">
              Leveres av
            </p>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {tracking.carrierName || "Din transportør"}
            </h1>
            <div
              className={`mt-5 inline-flex h-8 items-center justify-center gap-2 rounded-full px-3 text-sm font-semibold leading-none ${cancelled ? "bg-red-50 text-red-700" : "bg-[#eee8f7] text-[#553582]"}`}
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-current" />
              <span className="block leading-none">
                {statusLabels[tracking.status]}
              </span>
            </div>
            <div className="tracking-meta mt-7">
              <div>
                <p className="text-xs text-[#706879]">Ordre-ID</p>
                <p className="mt-1 font-semibold">
                  {tracking.orderNumber || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#706879]">
                  {multipleDeliveries ? "Leveringer" : "Forventet levering"}
                </p>
                <p className="mt-1 font-semibold">
                  {multipleDeliveries
                    ? `${deliveries.filter((stop) => stop.status === "COMPLETED").length} av ${deliveries.length} fullført`
                    : formatDate(tracking.delivery?.plannedDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#706879]">
                  {multipleDeliveries ? "Stopp på ordren" : "Leveringssted"}
                </p>
                <p className="mt-1 font-semibold">
                  {multipleDeliveries
                    ? `${tracking.stops?.length} stopp`
                    : formatAddress(tracking.deliveryAddress)}
                </p>
              </div>
            </div>
          </div>
          <div className="tracking-illustration">
            <img
              src="/images/tracking-delivery.svg"
              alt="Illustrasjon av en varebil som leverer en pakke"
            />
          </div>
        </section>
        {cancelled && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-900">
            <h2 className="font-bold">Ordren er kansellert</h2>
            <p className="mt-1 text-sm">
              Kontakt avsender dersom du har spørsmål om leveringen.
            </p>
          </div>
        )}
        <div className="tracking-detail-grid mt-6">
          <section
            className="tracking-card p-6 sm:p-8"
            aria-labelledby="tracking-heading"
          >
            <div className="mb-7 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[.16em] text-[#7253a6]">
                  Sporing
                </p>
                <h2 id="tracking-heading" className="mt-1 text-2xl font-bold">
                  Leveringsstatus
                </h2>
              </div>
              <button
                className="tracking-button text-sm"
                onClick={() => void load()}
              >
                Oppdater
              </button>
            </div>
            {tracking.stops?.length ? (
              <OrderTrackingStops stops={tracking.stops} />
            ) : (
              <ol aria-label="Leveringsforløp">
                {steps.map(([title, description], index) => {
                  const state = cancelled
                    ? index === 0
                      ? "is-complete"
                      : "is-future"
                    : index < current
                      ? "is-complete"
                      : index === current
                        ? "is-current"
                        : "is-future";
                  return (
                    <li
                      className={`tracking-step ${state}`}
                      key={title}
                      aria-current={
                        !cancelled && index === current ? "step" : undefined
                      }
                    >
                      <div className="tracking-step-dot">
                        <DotIcon check={!cancelled && index <= current} />
                      </div>
                      <div className="tracking-step-copy pl-4 pt-1">
                        <h3 className="font-bold">{title}</h3>
                        <p className="mt-1 text-sm leading-6 text-[#706879]">
                          {description}
                        </p>
                        {index === 4 && tracking.delivery?.plannedDate && (
                          <p className="mt-1 text-xs font-semibold text-[#7253a6]">
                            {formatDate(tracking.delivery.plannedDate)}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
          <aside
            className="tracking-card h-fit p-6 sm:p-8"
            aria-labelledby="recipient-heading"
          >
            <p className="text-xs font-bold uppercase tracking-[.16em] text-[#7253a6]">
              {multipleDeliveries ? "Kunde" : "Mottaker"}
            </p>
            <h2 id="recipient-heading" className="mt-1 text-xl font-bold">
              {tracking.recipient?.name || "Mottaker"}
            </h2>
            <dl className="mt-6 space-y-5 text-sm">
              <div>
                <dt className="text-[#706879]">Adresse</dt>
                <dd className="mt-1 font-medium leading-6">
                  {formatAddress(tracking.recipient?.address)}
                </dd>
              </div>
              {tracking.recipient?.email && (
                <div>
                  <dt className="text-[#706879]">E-post</dt>
                  <dd className="mt-1">
                    <a
                      className="tracking-link font-medium"
                      href={`mailto:${tracking.recipient.email}`}
                    >
                      {tracking.recipient.email}
                    </a>
                  </dd>
                </div>
              )}
              {tracking.recipient?.phoneNumber && (
                <div>
                  <dt className="text-[#706879]">Telefon</dt>
                  <dd className="mt-1">
                    <a
                      className="tracking-link font-medium"
                      href={`tel:${tracking.recipient.phoneNumber}`}
                    >
                      {tracking.recipient.phoneNumber}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
            {tracking.lastUpdated && (
              <p className="mt-7 border-t border-[#ebe6ef] pt-5 text-xs text-[#817989]">
                Sist oppdatert{" "}
                {new Intl.DateTimeFormat("nb-NO", {
                  dateStyle: "medium",
                  timeStyle: "short",
                }).format(new Date(tracking.lastUpdated))}
              </p>
            )}
          </aside>
        </div>
      </div>
    </main>
  );
}

function StateView({
  title,
  text,
  pulse = false,
}: {
  title: string;
  text?: string;
  pulse?: boolean;
}) {
  return (
    <main className="tracking-page grid min-h-screen place-items-center p-5">
      <div className="tracking-card w-full max-w-xl overflow-hidden text-center">
        <div className="bg-[#f0e9f8] px-8 pt-7">
          <img
            className={`mx-auto w-full max-w-xs ${pulse ? "animate-pulse" : ""}`}
            src="/images/tracking-delivery.svg"
            alt=""
          />
        </div>
        <div className="p-8">
          <img
            alt="Logibud"
            className="mx-auto mb-4 h-6 w-auto"
            src={logibudLogo}
          />
          <h1 className="text-2xl font-bold">{title}</h1>
          {text && (
            <p className="mx-auto mt-3 max-w-md leading-6 text-[#706879]">
              {text}
            </p>
          )}
        </div>
      </div>
    </main>
  );
}
